import { useState } from 'react';
import { Link, Outlet, useLocation, Navigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import styles from './AdminLayout.module.css';

export default function AdminLayout() {
    const { t } = useLanguage();
    const { user, isLoggedIn } = useAuth();
    const location = useLocation();
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    // Check admin access
    if (!isLoggedIn) {
        return <Navigate to="/" replace />;
    }

    if (user?.role !== 'admin') {
        return (
            <div className={styles.accessDenied}>
                <h2>{t('admin.accessDenied') || 'Access Denied'}</h2>
                <p>{t('admin.adminOnly') || 'This area is only accessible to administrators.'}</p>
                <Link to="/" className={styles.backLink}>
                    {t('admin.backToHome') || 'Back to Home'}
                </Link>
            </div>
        );
    }

    const navItems = [
        { path: '/admin', icon: '📊', label: t('admin.nav.dashboard') || 'Dashboard', exact: true },
        { path: '/admin/review', icon: '📝', label: t('admin.nav.review') || 'Review Queue' },
        { path: '/admin/management', icon: '🔧', label: t('admin.nav.management') || 'All Configs' },
        { path: '/admin/official', icon: '⭐', label: t('admin.nav.official') || 'Official Content' },
        { path: '/admin/users', icon: '👥', label: t('admin.nav.users') || 'Users' },
    ];

    const isActive = (path, exact = false) => {
        if (exact) return location.pathname === path;
        return location.pathname.startsWith(path);
    };

    return (
        <div className={styles.adminLayout}>
            {/* Sidebar */}
            <aside className={`${styles.sidebar} ${sidebarCollapsed ? styles.collapsed : ''}`}>
                <div className={styles.sidebarHeader}>
                    <Link to="/admin" className={styles.logo}>
                        {sidebarCollapsed ? 'FB' : 'Fat Boy Admin'}
                    </Link>
                    <button
                        className={styles.collapseBtn}
                        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                    >
                        {sidebarCollapsed ? '→' : '←'}
                    </button>
                </div>

                <nav className={styles.sidebarNav}>
                    {navItems.map(item => (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`${styles.navItem} ${isActive(item.path, item.exact) ? styles.active : ''}`}
                        >
                            <span className={styles.navIcon}>{item.icon}</span>
                            {!sidebarCollapsed && <span className={styles.navLabel}>{item.label}</span>}
                        </Link>
                    ))}
                </nav>

                <div className={styles.sidebarFooter}>
                    <Link to="/" className={styles.backToSite}>
                        <span className={styles.navIcon}>🏠</span>
                        {!sidebarCollapsed && <span>{t('admin.nav.backToSite') || 'Back to Site'}</span>}
                    </Link>
                </div>
            </aside>

            {/* Main Content */}
            <main className={styles.mainContent}>
                <Outlet />
            </main>
        </div>
    );
}
